import { XCircle } from 'lucide-react';
import React, { useState } from 'react';

import { CitationViewer } from '@/components/agent-chat/citations/CitationViewer';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCitationDetails } from '@/hooks/useCitationDetails';
import type { TMaterializedCitation } from '@/services/types/agent-chat-types';

interface CitationInlineViewerProps {
  matterId: string;
  citationIds: string[];
  activeCitationId: string | undefined;
  setActiveCitationId: (id: string | undefined) => void;
  setActiveFindingId: (id: string | undefined) => void;
}

interface CitationTooltipProps {
  citation: TMaterializedCitation | undefined;
  children: React.ReactNode;
}

const CitationTooltip: React.FC<CitationTooltipProps> = ({
  citation,
  children,
}) => {
  if (!citation) {
    return children;
  }

  const pageNumbers = citation.blocks
    .map((block) => block.page_number)
    .filter((page, index, array) => array.indexOf(page) === index)
    .sort((a, b) => a - b);

  // Derive label for pages without nested ternaries for clarity
  const pageText = (() => {
    if (pageNumbers.length === 1) {
      return `Page ${pageNumbers[0]}`;
    }

    if (pageNumbers.length > 1) {
      return `Pages ${pageNumbers.join(', ')}`;
    }

    return 'Document';
  })();

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="text-xs">
          <div className="font-semibold truncate">{citation.name}</div>
          <div className="text-gray-300 mt-1">{pageText}</div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export const CitationInlineViewer: React.FC<CitationInlineViewerProps> = ({
  matterId,
  citationIds,
  activeCitationId,
  setActiveCitationId,
  setActiveFindingId,
}) => {
  const [selectedHighlightId, setSelectedHighlightId] = useState<string>('');

  const { citationMap } = useCitationDetails(matterId, citationIds);

  const activeCitationData = activeCitationId
    ? citationMap[activeCitationId]
    : undefined;

  if (!activeCitationId || !activeCitationData) return null;

  return (
    <div className="mt-4 min-h-[300px] max-h-[1000px] h-[800px] overflow-auto rounded-lg border relative resize-y">
      {/* Citation Viewer Header */}
      <div className="flex items-center justify-between p-4">
        {/* Multiple citation selector */}
        {citationIds.length > 1 && (
          <div className="flex gap-1">
            {citationIds.map((cid, idx) => (
              <CitationTooltip key={cid} citation={citationMap[cid]}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveCitationId(cid);
                    setSelectedHighlightId(cid);
                  }}
                  className={`h-6 w-6 rounded-full text-xs font-semibold border flex items-center justify-center ${
                    cid === activeCitationId
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {idx + 1}
                </button>
              </CitationTooltip>
            ))}
          </div>
        )}

        {/* Active citation file name */}
        <span className="ml-4 font-medium truncate max-w-m">
          {activeCitationData?.name ?? 'Citation'}
        </span>

        <button
          type="button"
          onClick={() => {
            setActiveCitationId(undefined);
            setActiveFindingId(undefined);
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm hover:bg-gray-100"
          aria-label="Close citation"
        >
          <XCircle className="h-4 w-4" />
        </button>
      </div>

      <div className="w-full h-full">
        <CitationViewer
          url={activeCitationData.url}
          citations={activeCitationData.blocks}
          selectedCitationId={
            selectedHighlightId || activeCitationData.blocks[0]?.block_id
          }
          setSelectedCitationId={setSelectedHighlightId}
        />
      </div>
    </div>
  );
};
